import type { TemplateDecisionClass, TemplateDecisionRowDto } from '@pams/shared';
import type { TotalsContext } from './decisionRules';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [TEMPLATE-FIND] WHAT A DECISION ROW SAYS ABOUT ITS TEMPLATES — pure, so a test reads every branch with
 *  no render, and `react-refresh/only-export-components` keeps `DecisionContext.tsx` to its component.
 *
 *  ⚠️ A PRINTED TOTAL AND A DUTY POINT ARE ABOUT ONE PLACE IN ONE TEMPLATE. Their row carries ONE link —
 *  "Show it in template t152", to that check or that row, highlighted — and no "Used by" line, whose link
 *  would reach the same template a second way (`NameCell`'s one-destination rule). Every other class is
 *  shared by many templates, so its "Used by" line links each number it names.
 *
 *  ⚠️ "DIFFERS" IS THE TEMPLATE PANE'S OWN RULE — `printed !== null && printed !== parsed`, the condition its
 *  Totals check marks `data-differs` with — so the Decisions page and the pane bold the same columns.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** A class each of whose decisions is about one place in one template — a printed total, a duty point. */
export const isOneTemplateClass = (decisionClass: TemplateDecisionClass): boolean =>
  decisionClass === 'TOTALS' || decisionClass === 'DUTY_POINT';

type TotalsColumn = TotalsContext['columns'][number];

/** A column the booklet printed a figure for — the only kind that can differ or match. */
export type PrintedTotalsColumn = TotalsColumn & { readonly printed: number };

const printedOf = (column: TotalsColumn): column is PrintedTotalsColumn => column.printed !== null;

/**
 * A printed total's columns, split as a person checks them: the ones that DIFFER (each said on its own
 * line), the ones that MATCH (folded onto one), and the ones the booklet printed no figure for. Each
 * keeps the server's column order.
 */
export const totalsColumnsOf = (
  context: Pick<TotalsContext, 'columns'>,
): { differs: PrintedTotalsColumn[]; matches: PrintedTotalsColumn[]; notPrinted: TotalsColumn[] } => {
  const printed = context.columns.filter(printedOf);
  return {
    differs: printed.filter((column) => column.printed !== column.parsed),
    matches: printed.filter((column) => column.printed === column.parsed),
    notPrinted: context.columns.filter((column) => !printedOf(column)),
  };
};

/**
 * The template numbers a row names — the server sends a few (`templateKeys`) beside the true count
 * (`usedBy`), so the rest are a number, said as text: no list of them exists to link.
 */
export const usedByKeysOf = (
  row: Pick<TemplateDecisionRowDto, 'usedBy' | 'templateKeys'>,
): { shown: readonly string[]; more: number } => ({
  shown: row.templateKeys,
  more: Math.max(0, row.usedBy - row.templateKeys.length),
});
